import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { subscriptionService } from '../services/subscriptionService'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CheckCircle, Lock, Star, Crown, Zap } from 'lucide-react'
import { cn } from '@/lib/utils'

const SubscriptionGate = ({ 
  children, 
  requiredTier = 'Basic',
  contentTitle = 'this content',
  className = ''
}) => {
  const { user, isAuthenticated } = useAuth()
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [hasAccess, setHasAccess] = useState(false)

  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated || !user) {
        setHasAccess(false)
        setLoading(false)
        return
      }

      try {
        const userSubscription = await subscriptionService.getUserSubscription(user.id)
        setSubscription(userSubscription)
        
        if (userSubscription) {
          // past_due is treated the same as unsubscribed — no grace period
          const isPastDue = userSubscription.subscriptionStatus === 'past_due'
          const meetsRequiredTier = subscriptionService.hasRequiredTier(
            userSubscription.subscriptionTier,
            requiredTier
          )
          setHasAccess(!isPastDue && meetsRequiredTier)
        } else {
          setHasAccess(false)
        }
      } catch (error) {
        console.error('Error checking subscription:', error)
        setHasAccess(false)
      } finally {
        setLoading(false)
      }
    }

    checkSubscription()
  }, [user, isAuthenticated, requiredTier])

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center p-8", className)}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  // User not authenticated
  if (!isAuthenticated) {
    return (
      <Card className={cn("max-w-md mx-auto", className)}>
        <CardHeader className="text-center">
          <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <CardTitle>Sign in Required</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            You need to sign in to access {contentTitle}.
          </p>
          <Button onClick={() => window.location.href = '/pricing'} className="w-full">
            Sign In
          </Button>
        </CardContent>
      </Card>
    )
  }

  // User has access - render the protected content
  if (hasAccess) {
    return children
  }

  // User needs subscription upgrade
  const upgradeSuggestions = subscriptionService.getUpgradeSuggestions(
    subscription?.subscriptionTier || 'None',
    requiredTier
  )

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'Basic': return <Zap className="h-5 w-5" />
      case 'Standard': return <Star className="h-5 w-5" />
      case 'Premium': return <Crown className="h-5 w-5" />
      default: return <Lock className="h-5 w-5" />
    }
  }

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Basic': return 'bg-blue-500'
      case 'Standard': return 'bg-purple-500'
      case 'Premium': return 'bg-gold-500 bg-gradient-to-r from-yellow-400 to-orange-400'
      default: return 'bg-gray-500'
    }
  }

  return (
    <div className={cn("max-w-2xl mx-auto", className)}>
      <Card className="border-2 border-dashed border-muted">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className={cn(
              "p-3 rounded-full text-white",
              getTierColor(requiredTier)
            )}>
              {getTierIcon(requiredTier)}
            </div>
          </div>
          <CardTitle className="text-2xl">
            {requiredTier} Subscription Required
          </CardTitle>
          <p className="text-muted-foreground mt-2">
            You need a {requiredTier} subscription or higher to access {contentTitle}.
          </p>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Current vs Required */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Current Plan</p>
              <Badge variant="outline" className="mt-1">
                {subscription?.subscriptionTier || 'Free'}
              </Badge>
            </div>
            <div className="text-muted-foreground">→</div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Required Plan</p>
              <Badge className={cn(
                "mt-1 text-white",
                getTierColor(requiredTier)
              )}>
                {requiredTier}
              </Badge>
            </div>
          </div>

          {/* Benefits */}
          <div>
            <h4 className="font-semibold mb-3">What you'll get with {requiredTier}:</h4>
            <ul className="space-y-2">
              {upgradeSuggestions.benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button 
              className="w-full" 
              size="lg"
              onClick={() => window.location.href = upgradeSuggestions.upgradeUrl}
            >
              Upgrade to {requiredTier}
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => window.history.back()}
            >
              Go Back
            </Button>
          </div>

          {/* Note for current users */}
          {subscription?.subscriptionTier && subscription.subscriptionTier !== 'None' && (
            <div className="text-center pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                You currently have a {subscription.subscriptionTier} subscription.
                <br />
                Upgrade to unlock this premium content.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default SubscriptionGate